'use client';

import { AdCardEditButton } from '@/features/assistant/_components/rich/ad-card-edit-button';
import { AdCardEditTargetContext } from '@/features/assistant/_components/rich/ad-card-edit-context';
import { useSavedAdPreview } from '@/features/assistant/_components/rich/use-saved-ad-preview';
import type { DraftPromotion } from '@/features/meta-ads/api/launch-ad/draft-promotion';
import { usePublishDraftPromotion } from '@/features/meta-ads/api/launch-ad/use-publish-draft-promotion';
import { AlertTriangle } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

import { trackRecommendationEdited } from '../telemetry';
import { CreativePreview } from './creative-preview';
import { usePublishAd } from './use-publish-ad';
import { useSaveAdDraft } from './use-save-ad-draft';
import { useUpdatePendingAd } from './use-update-pending-ad';

export interface AdPreviewCardState {
  draftId: string;
  name: string;
  headline: string | null;
  primaryText: string | null;
  videoId: string | null;
}

interface AdPreviewCardProps {
  draft: AdPreviewCardState;
  onSendMessage?: (text: string) => void;
}

/**
 * Window 7 — minimal preview card rendered in the chat thread when
 * Claire calls `show_ad_preview`. Editable fields: headline + caption
 * (primaryText). Anything else stays as Claire configured it; owners
 * who want broader edits use `/ads/new` (Decision #14).
 */
export function AdPreviewCard({ draft, onSendMessage }: AdPreviewCardProps) {
  const current = useSavedAdPreview({
    variant: 'draft',
    adId: draft.draftId,
    adName: draft.name,
    headline: draft.headline ?? undefined,
    primaryText: draft.primaryText ?? undefined,
    videoId: draft.videoId ?? undefined,
  });
  // Keep the owner's in-progress changes; untouched fields follow the saved row.
  const [edits, setEdits] = useState<{
    headline?: string;
    primaryText?: string;
  }>({});
  const headline = edits.headline ?? current.headline ?? '';
  const primaryText = edits.primaryText ?? current.primaryText ?? '';
  const [published, setPublished] = useState(false);
  const emitEditedIfDirty = () => {
    const editedFields = Object.keys(edits).filter(
      (field) => edits[field as keyof typeof edits] !== undefined
    );
    if (editedFields.length === 0) return;
    trackRecommendationEdited({
      surface: 'chat',
      kind: 'ad_flow_service_pick',
      rankedServiceId: draft.draftId,
      rank: 1,
      acceptedAtRank: 1,
      editedFields,
    });
  };

  const updatePending = useUpdatePendingAd(draft.draftId);
  const saveDraft = useSaveAdDraft(draft.draftId);
  /** Set when publishing was refused because the promotion is a draft. */
  const [draftPromotion, setDraftPromotion] = useState<DraftPromotion | null>(
    null
  );
  const publishAd = usePublishAd(draft.draftId, {
    onSuccess: () => {
      setDraftPromotion(null);
      setPublished(true);
    },
    onDraftPromotion: setDraftPromotion,
  });
  const {
    publishAsync: publishPromotionAsync,
    isPublishing: isPublishingPromo,
  } = usePublishDraftPromotion();

  const isBusy =
    saveDraft.isPending || publishAd.isPending || isPublishingPromo;

  /** The owner saw the promotion named and chose to publish it with the ad. */
  const handlePublishPromotionAndAd = async () => {
    if (!draftPromotion) return;
    try {
      await publishPromotionAsync(draftPromotion.offerId);
    } catch (error) {
      toast.error(
        error instanceof Error && error.message
          ? error.message
          : 'Failed to publish the promotion'
      );
      return;
    }
    publishAd.mutate(edits);
  };

  const markSaved = (saved: typeof edits) => {
    setEdits((previous) => ({
      ...previous,
      ...(previous.headline === saved.headline ? { headline: undefined } : {}),
      ...(previous.primaryText === saved.primaryText
        ? { primaryText: undefined }
        : {}),
    }));
  };

  const handleSave = () => {
    emitEditedIfDirty();
    saveDraft.mutate(edits, { onSuccess: () => markSaved(edits) });
  };

  const handlePublish = () => {
    emitEditedIfDirty();
    publishAd.mutate(edits);
  };

  return (
    <AdCardEditTargetContext.Provider value={draft.draftId}>
      <Card data-testid="ad-preview-card" className="max-w-md my-3 bg-card">
        <CardContent className="p-4 space-y-4">
          <div className="flex items-baseline justify-between gap-2">
            <h3 className="text-base font-medium truncate">{current.adName}</h3>
            <AdCardEditButton
              adName={current.adName}
              field="ad name"
              onSendMessage={onSendMessage}
            />
          </div>

          <div className="relative">
            <CreativePreview videoId={current.videoId ?? null} />
            <div className="absolute right-2 top-2 rounded-sm bg-background/90">
              <AdCardEditButton
                adName={current.adName}
                field="creative"
                onSendMessage={onSendMessage}
              />
            </div>
          </div>

          <Field>
            <FieldLabel htmlFor={`ad-preview-headline-${draft.draftId}`}>
              Headline
              <AdCardEditButton
                adName={current.adName}
                field="headline"
                onSendMessage={onSendMessage}
              />
            </FieldLabel>
            <Input
              id={`ad-preview-headline-${draft.draftId}`}
              value={headline}
              onChange={(e) =>
                setEdits((previous) => ({
                  ...previous,
                  headline: e.target.value,
                }))
              }
              onBlur={() => {
                if (edits.headline !== undefined)
                  updatePending.mutate(
                    { headline: edits.headline },
                    { onSuccess: () => markSaved({ headline: edits.headline }) }
                  );
              }}
              maxLength={40}
              disabled={published}
              aria-invalid={false}
            />
          </Field>

          <Field>
            <FieldLabel htmlFor={`ad-preview-caption-${draft.draftId}`}>
              Caption
              <AdCardEditButton
                adName={current.adName}
                field="caption / ad copy"
                onSendMessage={onSendMessage}
              />
            </FieldLabel>
            <Textarea
              id={`ad-preview-caption-${draft.draftId}`}
              value={primaryText}
              onChange={(e) =>
                setEdits((previous) => ({
                  ...previous,
                  primaryText: e.target.value,
                }))
              }
              onBlur={() => {
                if (edits.primaryText !== undefined)
                  updatePending.mutate(
                    { primaryText: edits.primaryText },
                    {
                      onSuccess: () =>
                        markSaved({ primaryText: edits.primaryText }),
                    }
                  );
              }}
              rows={4}
              maxLength={500}
              disabled={published}
              aria-invalid={false}
            />
          </Field>
          {draftPromotion && !published ? (
            <div
              className="flex gap-2 rounded-md border border-amber-200/70 bg-amber-50/60 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200"
              data-testid="ad-preview-draft-promotion"
              role="status"
            >
              <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
              <p>
                Nothing was launched. This ad advertises the promotion &ldquo;
                {draftPromotion.offerName}&rdquo;, which is still a draft.
                Publish the promotion with the ad so customers can claim it.
              </p>
            </div>
          ) : null}
        </CardContent>
        <CardFooter className="flex gap-2 justify-end p-4 pt-0">
          <Button
            variant="outline"
            onClick={handleSave}
            disabled={isBusy || published}
          >
            {saveDraft.isPending ? 'Saving…' : 'Save draft'}
          </Button>
          {draftPromotion && !published ? (
            <Button
              onClick={() => void handlePublishPromotionAndAd()}
              disabled={isBusy}
            >
              {isBusy ? 'Publishing…' : 'Publish promotion and ad'}
            </Button>
          ) : (
            <Button onClick={handlePublish} disabled={isBusy || published}>
              {published
                ? 'Published'
                : publishAd.isPending
                  ? 'Publishing…'
                  : 'Publish'}
            </Button>
          )}
        </CardFooter>
      </Card>
    </AdCardEditTargetContext.Provider>
  );
}
